/// <amd-module name="Wiki/Renderer/VCPathHelper" />
import { RepoConstants } from "Wiki/ViewCommon/Constants";
import { getFileName } from "Wiki/ViewCommon/Utils/PathHelper";

/**
 * Combines a two paths. Resolves all "../" in the path segements.
 */
export function combine(path1: string | undefined, path2: string | undefined): string {
    if (!path1) {
        return path2 !== undefined ? path2 : "";
    }

    if (!path2) {
        return path1 !== undefined ? path1 : "";
    }

    let basePath = path1;
    let path = path2;
    if (path2.charAt(0) === RepoConstants.RootPath) {
        basePath = RepoConstants.RootPath;
        path = path2.substr(1);
    }

    const directory = _getDirectory(basePath);
    path = _resolveRelativePath(path, directory);

    return path;
}

/**
 * Returns the file extension for the give path
 * @param path
 */

export function getFileExtension(path: string): string {
    let extension = "";

    const fileName = getFileName(path);
    const dotIndex = fileName.lastIndexOf(".");
    if (dotIndex >= 0) {
        extension = fileName.substring(dotIndex + 1);
    }

    return extension;
}
/**
 * Combines a relativePath with a basePath and resolves all "../" path segements.
 */
// exported for testing
export function _resolveRelativePath(relativePath: string | undefined, basePath: string | undefined): string {
    if (!relativePath) {
        relativePath = "";
    }

    if (!basePath) {
        basePath = "";
    }

    const encodedPath = _uriEncodePathSegments(relativePath);
    const encodedBasePath = _uriEncodePathSegments(basePath);

    const absoluteUri = _pathJoin(encodedBasePath, encodedPath);
    return decodeURIComponent(absoluteUri);
}

/**
 * Gets the containing directory of the last path component.
 * @param path The path
 * @returns Path characters up to and including the last slash.
 *          Empty string if the path contains no slash.
 */
// exported for testing
export function _getDirectory(path: string | undefined): string {
    if (path) {
        const pos = path.lastIndexOf(RepoConstants.PathSeparator);
        if (pos >= 0) {
            return path.substr(0, pos + 1);
        }
    }

    return "";
}

/**
 * URI Encodes each segment in the path while preserving the '/' in between the segments or converting '\' to '/'
 */
function _uriEncodePathSegments(path: string): string {
    const segments = path.split(/[\\\/]/);

    for (let i = 0; i < segments.length; i++) {
        segments[i] = encodeURIComponent(segments[i]);
    }

    return segments.join("/");
}

/**
 * Combines two paths together. Allows for resolution of relative paths with "../" in their path.
 * @param basePath Base path argument. This will have segments removed from the end when the relative path uses "../".
 * @param relativePath relative path attached to the base path. Allows for resolution of paths where "../" is included.
 */
function _pathJoin(basePath: string, relativePath: string): string {
    let prepend = "";
    if (basePath.charAt(0) === RepoConstants.PathSeparator) {
        basePath = basePath.slice(1);
        prepend = RepoConstants.PathSeparator;
    }

    if (relativePath.charAt(0) === RepoConstants.PathSeparator) {
        relativePath = relativePath.slice(1); // Array.join will take care of "/"'s in the path
    }

    let numberOfFilesInPath = 0;
    if (basePath.charAt(basePath.length - 1) === RepoConstants.PathSeparator) {
        basePath = basePath.slice(0, basePath.length - 1);
    } else if (basePath.length > 0) {
        numberOfFilesInPath = 1;
    }

    const baseSegments = basePath.length ? basePath.split(/[\\\/]/) : []; // split will default to an array containing "", we dont want that.
    const relativeSegments = relativePath.split(/[\\\/]/);
    let up = true;
    let baseTraversals = 0;
    for (let i = 0; up && i < relativeSegments.length; i++) {
        if (relativeSegments[i] === ".") {
            relativeSegments.splice(i, 1);
            i--;
        } else if (relativeSegments[i] === "..") {
            baseTraversals++;
        } else {
            up = false;
        }
    }
    let base: string[] = [];
    if (baseTraversals < baseSegments.length) {
        base = baseSegments.slice(0, baseSegments.length - numberOfFilesInPath - baseTraversals);
    }

    const relative = relativeSegments.slice(baseTraversals);
    const resultSegments = base.concat(relative);
    const result = resultSegments.join(RepoConstants.PathSeparator);

    return prepend + result;
}
